import { useEffect, useRef, useState } from 'react';
import { Loader2, TriangleAlert } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { verifyMagicLink } from '../lib/auth';

type Status = 'verifying' | 'invalid' | 'error';

/**
 * Spec 003 — magic-link callback (SC-010/011). The emailed link lands here (GET /auth/verify?token=…);
 * the page POSTs the token to consume it (never a bare-GET side effect — prefetch-safe). Four states:
 * verifying (spinner) → on success, navigate into the account area; on failure, a specific
 * invalid/expired panel with a path back to sign in. WCAG 2.1 AA.
 */
export default function AuthVerify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('verifying');
  const panelRef = useRef<HTMLDivElement>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return; // consume exactly once (StrictMode double-invoke guard)
    ranRef.current = true;
    const token = params.get('token') ?? '';
    // Security (spec 003 review #3): strip the token from the address bar + history immediately, so a
    // one-time login token can't linger in browser history, bookmarks, or a referer header. We've
    // already captured it into `token`; the POST below still consumes it. (Referer is additionally
    // suppressed by Helmet's no-referrer default.)
    if (token) window.history.replaceState({}, '', '/auth/verify');
    if (!token) {
      setStatus('invalid');
      return;
    }
    void verifyMagicLink(token).then((result) => {
      if (result.kind === 'ok') {
        navigate('/account', { replace: true });
        return;
      }
      setStatus(result.kind === 'invalid' ? 'invalid' : 'error');
    });
  }, [params, navigate]);

  useEffect(() => {
    if (status !== 'verifying') panelRef.current?.focus();
  }, [status]);

  return (
    <div className="mx-auto w-full max-w-md px-6 py-24">
      {status === 'verifying' ? (
        <div className="flex flex-col items-center text-center" aria-live="polite">
          <Loader2 aria-hidden className="h-8 w-8 animate-spin text-accent" />
          <p className="mt-4 font-serif text-lg text-ink">Signing you in…</p>
        </div>
      ) : (
        <div
          ref={panelRef}
          tabIndex={-1}
          role="alert"
          className="rounded-lg border border-error/40 bg-error/5 p-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <TriangleAlert aria-hidden className="h-7 w-7 text-error" />
          <h1 className="mt-3 font-display text-lg font-semibold uppercase tracking-[0.1em] text-ink">
            {status === 'invalid' ? 'This link is invalid or expired' : 'Something went wrong'}
          </h1>
          <p className="mt-2 font-serif text-base leading-relaxed text-ink">
            {status === 'invalid'
              ? 'Sign-in links work once and expire after 15 minutes. Request a fresh one to continue.'
              : 'We couldn’t sign you in just now. Please try again in a moment.'}
          </p>
          <Link
            to="/login"
            className="mt-4 inline-block font-sans text-sm font-semibold text-accent underline decoration-accent underline-offset-4 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Back to sign in
          </Link>
        </div>
      )}
    </div>
  );
}
